import {useState, useEffect, useCallback, useRef} from "react";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {onlineProcessesService} from "../../services";
import {useLoader} from "@quadral/quadral-component-common/dist/components/loader/LoaderContext";
import QuadralList from "@quadral/quadral-component-common/dist/components/list";
import QuadralButton from "@quadral/quadral-component-common/dist/components/button";
import QuadralTextField from "@quadral/quadral-component-common/dist/components/textField";
import QuadralTypography from '@quadral/quadral-component-common/dist/components/typography';
import QuadralBadge from "@quadral/quadral-component-common/dist/components/badge";
import AdvancedFilters from "@quadral/quadral-component-common/dist/components/advancedFilters";
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass, faFileLines, faCirclePause, faUser, faCheck, faBan, faAt } from "@fortawesome/free-solid-svg-icons";

const FILTER_FIELDS = [
    {
        id: 'status',
        label: 'Statut',
        type: 'select',
        quick: true,
        options: [
            {value: 'en_attente_de_lancement', label: 'En attente de lancement'},
            {value: 'en_attente_de_connexion', label: 'En attente de connexion'},
            {value: 'en_attente_reponse', label: 'En attente de réponse'},
            {value: 'en_attente_des_pj', label: 'En attente des PJ'},
            {value: 'controle_en_cours', label: 'Contrôle en cours'},
            {value: 'complements_attendu', label: 'Compléments attendu'},
            {value: 'complements_fournis', label: 'Compléments fournis'},
            {value: 'reponse_validee', label: 'Validée'},
            {value: 'controlee', label: 'Contrôlée'},
            {value: 'annulee', label: 'Annulée'}
        ]
    },
    {
        id: 'deviationStatus',
        label: "Type d'escalade",
        type: 'multiselect',
        quick: true,
        options: [
            {value: 'Escalade_interne', label: 'Escaladé interne'},
            {value: 'Escalade_externe', label: 'Escaladé mandant'},
            {value: 'Retours_a_analyser_interne', label: 'Retours à analyser - interne'},
            {value: 'Retours_a_analyser_externe', label: 'Retours à analyser - mandant'},
            {value: 'Traite', label: 'Traité'}
        ]
    },
    {
        id: 'communicationChannel',
        label: 'Canal',
        type: 'select',
        quick: true,
        options: [
            {value: 'papier', label: 'Papier'},
            {value: 'email', label: 'Email'}
        ]
    },
    {
        id: 'surveyType',
        label: "Type d'enquête",
        type: 'select',
        quick: false,
        options: [
            {value: 'Sociale', label: 'Sociale'},
            {value: 'SLS', label: 'SLS'},
            {value: 'S', label: 'S'}
        ]
    },
    {id: 'phone', label: 'Téléphone', type: 'string', quick: false},
    {id: 'company', label: 'Société', type: 'string', quick: false},
    {id: 'agency', label: 'Agence', type: 'string', quick: false},
    {id: 'antenna', label: 'Antenne', type: 'string', quick: false},
    {id: 'city', label: 'Ville', type: 'string', quick: false},
    {id: 'effectiveResponseDate', label: 'Date de réponse effective', type: 'date', quick: false},
    {id: 'lastModificationDate', label: 'Date de dernière modification', type: 'date', quick: false}
];

const buildDefaultFilters = () => {
    const obj = {};
    FILTER_FIELDS.forEach(f => {
        obj[f.id] = f.type === 'multiselect' ? [] : '';
    });
    return obj;
};

const SurveyParticipations = () => {
    const timeoutRef = useRef(null);
    const hasLoadedRef = useRef(false);
    const navigate = useNavigate();
    const {surveyId} = useParams();
    const {showLoader, hideLoader} = useLoader();
    const [references, setReferences] = useState({});

    useEffect(() => {
        onlineProcessesService.getReferences()
            .then((res) => {
                setReferences(res);
            })
            .catch((err) => {
                console.error('Erreur lors de la récupération des références:', err);
            });
    }, []);

    const statusMap = references?.SURVEY_PARTICIPATION_STATUS?.reduce((acc, item) => {
        acc[item.code] = item.label;
        return acc;
    }, {}) || {};

    const deviationStatusMap = {
        Escalade_interne: 'Escaladé interne',
        Escalade_externe: 'Escaladé mandant',
        Retours_a_analyser_interne: 'Retours à analyser - interne',
        Retours_a_analyser_externe: 'Retours à analyser - mandant',
        Traite: 'Traité',
    };

    const DEVIATION_COLOR_MAP = {
        Escalade_interne: 'purple',
        Escalade_externe: 'purple',
        Retours_a_analyser_interne: 'coral',
        Retours_a_analyser_externe: 'coral',
        Traite: 'teal',
    };

    const STATUS_COLOR_MAP = {
        "controle_en_cours": 'control',
        "en_attente_des_pj": 'warning',
        "complements_attendu": 'warning',
        "en_attente_de_lancement": 'info',
        "en_attente_de_connexion": 'info',
        "en_attente_reponse": 'info',
        "premier_courrier_recu": 'info',
        "complements_fournis": 'info',
        "controlee": 'info',
        "reponse_validee": 'success',
        "annulee": 'secondary',
    };

    const STATUS_ICON_MAP = {
        "controle_en_cours": faFileLines,
        "en_attente_des_pj": faCirclePause,
        "complements_attendu": faFileLines,
        "en_attente_de_lancement": faCirclePause,
        "en_attente_de_connexion": faUser,
        "en_attente_reponse": faFileLines,
        "premier_courrier_recu": faFileLines,
        "complements_fournis": faFileLines,
        "controlee": faCheck,
        "reponse_validee": faCheck,
        "annulee": faBan,
    };

    const buildColumns = (navigate) => [
        {id: 'lastname', label: 'Nom et Prénom', sortable: true},
        {id: 'contractCode', label: 'Code contrat', sortable: true},
        {
            id: 'assetAddressStreet',
            label: 'Adresse',
            render: (row) => `${row.assetAddressNumber} ${row.assetAddressStreet}, ${row.assetAddressPostcode}`
        },
        {
            id: 'birthdate',
            label: 'Date de naissance',
            sortable: true,
            render: (row) => row.birthdate ? new Date(row.birthdate).toLocaleDateString('fr-FR') : '-'
        },
        {
            id: 'status', 
            label: 'Statut de participation', 
            sortable: true,
            render: (row) => (
                <QuadralBadge 
                    color={STATUS_COLOR_MAP[row.status] || 'secondary'} 
                    icon={<FontAwesomeIcon icon={STATUS_ICON_MAP[row.status] || faFileLines} />}  
                    label={statusMap[row.status] || row.status} 
                />
            )
        },
        {
            id: 'deviationStatus', 
            label: "Type d'escalade", 
            sortable: true,
            render: (row) => (
                <QuadralBadge
                    color={DEVIATION_COLOR_MAP[row.deviationStatus] || 'pink'}
                    label={deviationStatusMap[row.deviationStatus] || (row?.deviationStatus ? row.deviationStatus : 'Traitement normal')}
                />
            )
        },
        {
            id: 'communicationChannel', 
            label: 'Canal', 
            sortable: true,
            render: (row) => {
                const channel = row?.communicationChannel;
                if (!channel) return null;

                const icon = {
                    email: faAt,
                    papier: faFileLines,
                }[channel];

                return (
                    <QuadralBadge
                        label={channel}
                        color="secondary"
                        style={{color:'#000', textTransform: 'capitalize'}}
                        icon={<FontAwesomeIcon icon={icon}/>}
                    />
                );
            }
        },
        {
            id: 'lastModificationDate',
            label: 'Dernière modification',
            sortable: true,
            render: (row) =>
                row.lastModificationDate
                    ? new Date(row.lastModificationDate).toLocaleString('fr-FR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                    })
                    : '-'
        },
        {
            id: 'actions',
            label: '',
            render: (row) => (
                <QuadralButton onClick={() => navigate(`/enquetes/${row.surveyId}/participations/${row.id}`)}>
                    Ouvrir
                </QuadralButton>
            )
        }
    ];

    const [searchParams, setSearchParams] = useSearchParams();    
    const getFiltersFromURL = () => {
        const obj = buildDefaultFilters();

        FILTER_FIELDS.forEach(f => {
            const raw = searchParams.get(f.id);
            if (!raw) return;

            if (f.type === 'multiselect') {
                obj[f.id] = raw.split(',');
            } else {
                obj[f.id] = raw;
            }
        });

        return obj;
    };

    const [filterValues, setFilterValues] = useState(() => getFiltersFromURL());
    const [participations, setParticipations] = useState([]);
    const [hasMoreResults, setHasMoreResults] = useState(false);
    const [searchValue, setSearchValue] = useState('');

    const [pagination, setPagination] = useState({
        limit: 7, offset: 0
    });
    const [sort] = useState({
        field: 'status', direction: 'ASC'
    });

    const syncToURL = (filters) => {
        const params = new URLSearchParams();

        Object.entries(filters).forEach(([key, val]) => {
            if (val === '' || val === null || val === undefined) return;

            if (Array.isArray(val)) {
                if (val.length) params.set(key, val.join(','));
            } else {
                params.set(key, val);
            }
        });

        setSearchParams(params, {replace: true});
    };

    const fetchParticipations = useCallback(
        (fv = filterValues, search = searchValue, getMoreRows = false, paginationOverride = null) => {
            const resetPagination = { ...pagination, offset: 0 };
            const effectivePagination = getMoreRows
                ? paginationOverride || pagination
                : resetPagination;
            if (!getMoreRows) {
                setPagination(resetPagination);
                setParticipations([]);
            }

            showLoader();

            const criteria = {
                surveyId,
                pagination: effectivePagination,
                sort,
                ...(search ? { contractCode: search } : {})
            };

            Object.entries(fv).forEach(([key, val]) => {
                if (val === '' || val === null || val === undefined) return;
                if (Array.isArray(val)) {
                    if (val.length > 0) criteria[key] = val;
                } else {
                    criteria[key] = val;
                }
            });

            onlineProcessesService
                .searchSurveyParticipations(criteria)
                .then((res) => {
                    const rows = res?.surveyParticipations || [];
                    setParticipations(prev => (getMoreRows ? [...prev, ...rows] : rows));
                    setHasMoreResults(res?.hasMoreResults || false);
                })
                .catch((err) => {
                    console.error(err);
                })
                .finally(() => {
                    hideLoader();
                });
        },
        [surveyId, filterValues, searchValue, pagination, sort, showLoader, hideLoader]
    );

    function getMoreRows() {
        const nextPagination = {
            ...pagination,
            offset: pagination.offset + pagination.limit
        };
        setPagination(nextPagination);
        fetchParticipations(filterValues, searchValue, true, nextPagination);
    }

    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
        };
    }, []);

    useEffect(() => {
        if (hasLoadedRef.current) return;
        hasLoadedRef.current = true;
        fetchParticipations(filterValues, searchValue);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleFilterChange = (fieldId, value) => {
        setFilterValues((prev) => ({...prev, [fieldId]: value}));
    };

    const handleApply = (values) => {
        setFilterValues(values);
        syncToURL(values);
        fetchParticipations(values, searchValue);
    };

    const handleReset = () => {
        const empty = buildDefaultFilters();

        setFilterValues(empty);
        setSearchValue('');
        setSearchParams({}, {replace: true});

        fetchParticipations(empty, '');
    };

    const handleSearch = (value) => {
        setSearchValue(value);

        const params = new URLSearchParams(searchParams);
        if (value) params.set('contractCode', value);
        else params.delete('contractCode');

        setSearchParams(params, {replace: true});

        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }

        timeoutRef.current = setTimeout(() => {
            fetchParticipations(filterValues, value);
        }, 600);
    };

    return (
        <>
            <QuadralTypography variant="modalTitle" gutterBottom> Participations</QuadralTypography>
            <QuadralTextField
                style={{backgroundColor: '#fff'}}
                innerStyle={{ fontSize: "12px" }}
                value={searchValue}
                onChange={handleSearch}
                startIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                placeholder="Rechercher par code contrat"
                noPadding
            />

            <AdvancedFilters
                fields={FILTER_FIELDS}
                values={filterValues}
                onChange={handleFilterChange}
                onApply={handleApply}
                onReset={handleReset}
                onSearch={handleSearch}
                searchValue={searchValue}
            />

            <QuadralList
                columns={buildColumns(navigate)}
                rows={participations}
                sortable={true}
                showMoreRows={hasMoreResults}
                showMoreRowsMethod={getMoreRows}
                numberOfRows={pagination.limit}
                rowIndent={(pagination.offset + pagination.limit)}
            />
        </>
    );
};

export default SurveyParticipations;